import { Box, ButtonGroup, IconButton, Tooltip } from '@cad-arc';
import React from 'react';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import { AdditionalInfoType, AdditionalInfoView, Priority, PRO_QA_TYPES } from '../../types';

export interface NarrativeActionMenuProps {
    info: AdditionalInfoView;
    currentUserId?: number;
    isPinned?: boolean;
    isScheduledEvent?: boolean;
    isBolo?: boolean;
    isFirstResponder?: boolean;
    isMergedEventNarrative?: boolean;
    /** Read-only surface: suppress every write action (edit/emphasize/pin/unpin/
     *  delete). "Go-To" (scroll-to) and "Open merged event" still render, since
     *  both are navigation (read) actions. Wired from the read-only narrative
     *  list (`isReadOnlyMode`) */
    readOnly?: boolean;
    onEdit?: () => void;
    onToggleEmphasis?: () => void;
    onPin?: () => void;
    onUnpin?: () => void;
    onScrollTo?: () => void;
    onOpenMergedEvent?: () => void;
    onDelete?: () => void;
    isDeleting?: boolean;
    isTogglingEmphasis?: boolean;
    isPinning?: boolean;
    isPinDisabledByCap?: boolean;
    pinDisabledTooltip?: string;
}

export function NarrativeActionMenu({
    info,
    currentUserId,
    isPinned = false,
    isScheduledEvent = false,
    isBolo = false,
    isFirstResponder = false,
    isMergedEventNarrative = false,
    readOnly = false,
    onEdit,
    onToggleEmphasis,
    onPin,
    onUnpin,
    onScrollTo,
    onOpenMergedEvent,
    onDelete,
    isDeleting = false,
    isTogglingEmphasis = false,
    isPinning = false,
    isPinDisabledByCap = false,
    pinDisabledTooltip,
}: NarrativeActionMenuProps): React.JSX.Element | null {
    const {
        rollOutCadNarrativeFilteringAndFocusImprovementsEnabled,
        editAdditionalInfos,
        pinUnpinAdditionalInfos,
        deleteAdditionalInfos,
    } = useEnabledFeatures();

    // Controlled so the pin-cap tooltip closes on mouse-leave/blur. The action
    // menu is display:none when the row isn't hovered; a portaled Radix tooltip
    // left open would lose its trigger's box and reflow to the top-left corner.
    const [isPinTooltipOpen, setIsPinTooltipOpen] = React.useState(false);

    if (!rollOutCadNarrativeFilteringAndFocusImprovementsEnabled) {
        return null;
    }

    const canEdit = editAdditionalInfos ?? false;
    const canPinUnpin = pinUnpinAdditionalInfos ?? false;
    // Default true to match existing behavior in event-panel-additional-infos.tsx
    // and bolo-panel-additional-infos.tsx
    const canDelete = deleteAdditionalInfos ?? true;

    const isAuthor = currentUserId != null && currentUserId === info.authorUserId;
    const isUserType = info.type === AdditionalInfoType.USER;
    const isProQA = PRO_QA_TYPES.includes(info.type as AdditionalInfoType);
    const isHighPriority = info.priority === Priority.HIGH;
    const isStandardPriority = info.priority === Priority.STANDARD;

    // Go-To (scroll-to) is a read action, so it survives read-only; every other
    // action mutates the narrative and is gated behind canModify.
    const canModify = !readOnly;

    const showScrollTo = isPinned && onScrollTo != null;
    const showEdit = canModify && canEdit && isAuthor && isUserType && onEdit != null;
    const showEmphasize =
        canModify &&
        canEdit &&
        isAuthor &&
        isUserType &&
        isStandardPriority &&
        onToggleEmphasis != null;
    const showRemoveEmphasis =
        canModify &&
        canEdit &&
        isAuthor &&
        isUserType &&
        isHighPriority &&
        onToggleEmphasis != null;
    const showPin = canModify && canPinUnpin && !isPinned && onPin != null;
    const showUnpin = canModify && canPinUnpin && isPinned && onUnpin != null;
    // Merged events are a regular-event-only concept — explicitly gate on
    // entity type rather than relying solely on parentAgencyEventId being
    // absent/zeroed upstream for BOLO and scheduled events. Opening the
    // merged event is a navigation (read) action, so — like Go-To — it
    // survives read-only mode and isn't gated on canModify.
    const showOpenMergedEvent =
        isMergedEventNarrative && !isBolo && !isScheduledEvent && onOpenMergedEvent != null;
    const showDelete =
        canModify &&
        canDelete &&
        isAuthor &&
        isUserType &&
        !isScheduledEvent &&
        !info.isEventNarrative &&
        !isProQA &&
        onDelete != null;

    const hasAnyAction =
        showScrollTo ||
        showEdit ||
        showEmphasize ||
        showRemoveEmphasis ||
        showPin ||
        showUnpin ||
        showOpenMergedEvent ||
        showDelete;

    if (!hasAnyAction) {
        return null;
    }

    const buttonSize = isFirstResponder ? 'lg' : 'sm';

    const pinButton = (
        <IconButton
            aria-label="Pin"
            data-testid="NARRATIVE_ACTION_PIN"
            icon="PinOutline"
            size={buttonSize}
            variant="ghost"
            onClick={onPin}
            isDisabled={isPinning || isPinDisabledByCap}
        />
    );

    return (
        <ButtonGroup
            isAttached
            className="narrative-action-menu"
            role="toolbar"
            aria-label="Narrative actions"
            data-testid="NARRATIVE_ACTION_MENU"
        >
            {showScrollTo && (
                <IconButton
                    aria-label="Scroll to"
                    data-testid="NARRATIVE_ACTION_SCROLL_TO"
                    icon="Crosshairs"
                    size={buttonSize}
                    variant="ghost"
                    onClick={onScrollTo}
                />
            )}
            {showEdit && (
                <IconButton
                    aria-label="Edit"
                    data-testid="NARRATIVE_ACTION_EDIT"
                    icon="Edit"
                    size={buttonSize}
                    variant="ghost"
                    onClick={onEdit}
                />
            )}
            {showEmphasize && (
                <IconButton
                    aria-label="Emphasize"
                    data-testid="NARRATIVE_ACTION_EMPHASIZE"
                    icon="ExclamationPoint"
                    size={buttonSize}
                    variant="ghost"
                    onClick={onToggleEmphasis}
                    isDisabled={isTogglingEmphasis}
                />
            )}
            {showRemoveEmphasis && (
                <IconButton
                    aria-label="Remove emphasis"
                    data-testid="NARRATIVE_ACTION_REMOVE_EMPHASIS"
                    icon="ExclamationPointOff"
                    size={buttonSize}
                    variant="ghost"
                    onClick={onToggleEmphasis}
                    isDisabled={isTogglingEmphasis}
                />
            )}
            {showPin &&
                (pinDisabledTooltip ? (
                    <Tooltip
                        content={pinDisabledTooltip}
                        side="top"
                        hasArrow
                        isOpen={isPinTooltipOpen}
                        onOpenChange={setIsPinTooltipOpen}
                    >
                        <Box
                            as="span"
                            display="inline-flex"
                            onMouseEnter={() => setIsPinTooltipOpen(true)}
                            onMouseLeave={() => setIsPinTooltipOpen(false)}
                            onFocus={() => setIsPinTooltipOpen(true)}
                            onBlur={() => setIsPinTooltipOpen(false)}
                        >
                            {pinButton}
                        </Box>
                    </Tooltip>
                ) : (
                    pinButton
                ))}
            {showUnpin && (
                <IconButton
                    aria-label="Unpin"
                    data-testid="NARRATIVE_ACTION_UNPIN"
                    icon="PinOutlineOff"
                    size={buttonSize}
                    variant="ghost"
                    onClick={onUnpin}
                    isDisabled={isPinning}
                />
            )}
            {showOpenMergedEvent && (
                <IconButton
                    aria-label="Open merged event"
                    data-testid="NARRATIVE_ACTION_OPEN_MERGED_EVENT"
                    icon="OpenNewWindow"
                    size={buttonSize}
                    variant="ghost"
                    onClick={onOpenMergedEvent}
                />
            )}
            {showDelete && (
                <IconButton
                    aria-label="Delete"
                    data-testid="NARRATIVE_ACTION_DELETE"
                    icon="TrashCan"
                    size={buttonSize}
                    variant="ghost"
                    onClick={onDelete}
                    isDisabled={isDeleting}
                />
            )}
        </ButtonGroup>
    );
}
